import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight, CalendarClock, ListChecks } from 'lucide-react';
import { API } from './groupMeta';

const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const GroupCalendar = ({ getAuthHeaders, onOpen }) => {
  const [events, setEvents] = useState([]);
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  useEffect(() => { axios.get(`${API}/calendar`, { headers: getAuthHeaders() }).then(r => setEvents(r.data)).catch(() => {}); }, []); // eslint-disable-line

  const days = useMemo(() => {
    const start = new Date(cursor); start.setDate(1 - ((cursor.getDay() + 6) % 7));
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  }, [cursor]);
  const byDay = useMemo(() => events.reduce((m, e) => ((m[e.date] = m[e.date] || []).push(e), m), {}), [events]);
  const today = iso(new Date());
  const shift = (n) => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + n, 1));
  const upcoming = events.filter(e => e.date >= today).slice(0, 8);

  return (
    <div className="grid xl:grid-cols-[1fr_300px] gap-4" data-testid="group-calendar">
      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.015] p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-medium" data-testid="calendar-month">{cursor.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</h3>
          <div className="flex gap-1">
            <button onClick={() => shift(-1)} data-testid="calendar-prev" className="p-1.5 rounded-lg border border-white/[0.08] text-white/60 hover:text-white"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={() => setCursor(new Date(new Date().getFullYear(), new Date().getMonth(), 1))} className="px-3 rounded-lg border border-white/[0.08] text-[11px] text-white/60">Today</button>
            <button onClick={() => shift(1)} data-testid="calendar-next" className="p-1.5 rounded-lg border border-white/[0.08] text-white/60 hover:text-white"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>
        <div className="grid grid-cols-7 gap-1 text-[9px] uppercase tracking-[0.15em] text-white/35 mb-1">{DOW.map(d => <div key={d} className="px-1">{d}</div>)}</div>
        <div className="grid grid-cols-7 gap-1">
          {days.map(d => {
            const k = iso(d); const ev = byDay[k] || [];
            const inMonth = d.getMonth() === cursor.getMonth();
            return (
              <div key={k} className={`min-h-[86px] rounded-lg p-1.5 border ${k === today ? 'border-cyan-400/40 bg-cyan-500/[0.05]' : 'border-white/[0.04]'} ${inMonth ? '' : 'opacity-35'}`}>
                <p className={`text-[10px] mb-1 ${k === today ? 'text-cyan-300 font-semibold' : 'text-white/45'}`}>{d.getDate()}</p>
                {ev.slice(0, 3).map((e, i) => (
                  <button key={i} onClick={() => onOpen(e.group_id)} title={`${e.group_name}: ${e.title}`} data-testid={`calendar-event-${k}-${i}`}
                    className={`w-full text-left truncate text-[9px] px-1.5 py-0.5 rounded mb-0.5 ${k < today ? 'bg-rose-500/20 text-rose-200' : e.kind === 'inspection' ? 'bg-violet-500/20 text-violet-100' : 'bg-cyan-500/15 text-cyan-100'}`}>
                    {e.kind === 'inspection' ? '◆ ' : '• '}{e.group_name}
                  </button>
                ))}
                {ev.length > 3 && <p className="text-[9px] text-white/40">+{ev.length - 3} more</p>}
              </div>
            );
          })}
        </div>
      </div>
      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.015] p-4">
        <p className="text-[9px] tracking-[0.2em] uppercase text-white/45 font-semibold mb-3">Coming up</p>
        {upcoming.length === 0 ? <p className="text-xs text-white/35">Nothing scheduled.</p> : upcoming.map((e, i) => (
          <button key={i} onClick={() => onOpen(e.group_id)} className="w-full flex items-start gap-3 py-2 border-b border-white/[0.04] last:border-0 text-left hover:bg-white/[0.02]">
            {e.kind === 'inspection' ? <CalendarClock className="w-4 h-4 text-violet-300 mt-0.5" /> : <ListChecks className="w-4 h-4 text-cyan-300 mt-0.5" />}
            <div className="min-w-0"><p className="text-xs text-white truncate">{e.title}</p>
              <p className="text-[10px] text-white/40">{e.group_name} · {new Date(e.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</p></div>
          </button>
        ))}
      </div>
    </div>
  );
};
